import React, { FC, useCallback, useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { Container, styled } from '@mui/material';
import { InstantSearch } from 'react-instantsearch';
import { useLocation, useParams, useNavigate } from 'react-router-dom';

import NotFoundPage from '~/blocks/NotFoundPage/NotFoundPage';
import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import AnimatedSkeletonMultiline from '~/components/AnimatedSkeletonMultiline';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import Hero from '~/components/Hero/Hero';
import { CenteredContainer } from '~/components/Hero/style';
import RouteWithCode from '~/components/RouteWithCode/RouteWithCode';
import Search from '~/components/Search/Search';
import SeoBlock from '~/components/SeoBlock/SeoBlock';
import { DEFAULT_DISCOVER_URL, FILTERS, RM_PAGES } from '~/const';
import { useDiscoverContext } from '~/context/DiscoverContext';
import { useRmMetadata } from '~/context/RmContext';
import useScriptLoader, { SCRIPTS } from '~/context/ScriptLoader/useScriptLoader';
import { useSsrContext } from '~/context/SsrContext';
import { GET_CATEGORIES_BY_PARENT, GET_VIDEO_CATEGORY } from '~/graphql/videos';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { CategoryInfo } from '~/pages/DiscoverPage/components/CategoryInfo/CategoryInfo';
import SERPContainer from '~/pages/DiscoverPage/components/SERPContainer/SERPContainer';
import SubcategoriesBlock from '~/pages/DiscoverPage/components/SubcategoriesBlock/SubcategoriesBlock';
import SustainableGoal from '~/pages/DiscoverPage/components/SustainableGoal';
import Tabs from '~/pages/DiscoverPage/components/Tabs/Tabs';
import { InstantSearchOnStateChangeProp } from '~/pages/DiscoverPage/const';
import { buildCategoryRoute, buildDiscoverRoute, DISCOVER_SEARCH_ROUTE } from '~/routes';
import { css, CssUtilityType } from '~/styled';
import {
  LARGE_MARGIN_PX,
  MIDDLE_MARGIN_PX,
  SKELETON_TEXT_HEIGHT_PX,
  SKELETON_TEXT_HEIGHT_SMALL_PX,
  SMALL_MARGIN_PX,
  TINY_MARGIN_PX,
} from '~/theme';
import {
  Maybe,
  Query,
  QueryGetVideoCategoryArgs,
  QueryListVideoCategoriesArgs,
  Tenant,
  VideoCategory,
} from '~/types';
import { generateSeoTitle } from '~/utils/generateSeoTitle';
import redirectToNewSlug from '~/utils/redirectToNewSlug';
import { TENANTS_CONFIG, TenantsRefs } from '~/utils/tenantsConfig';
import { translate } from '~/utils/translate';
import getCanonicalUrl from '~/utils/urlUtils/getCanonicalUrl';

const attributesExcludedFromClear = [FILTERS.Category];

const getNavigationArray = (categoryInfo?: Maybe<VideoCategory>) => {
  let navigationArr = [
    {
      title: translate('page.discover.discover'),
      url: DEFAULT_DISCOVER_URL,
    },
  ];

  if (categoryInfo?.parent) {
    navigationArr.push({
      title: categoryInfo?.parent?.title,
      url: buildDiscoverRoute(categoryInfo?.parent?.slug),
    });
  }

  if (categoryInfo) {
    navigationArr.push({
      title: categoryInfo.title,
      url: buildCategoryRoute(categoryInfo?.parent?.slug, categoryInfo.slug),
    });
  }

  return navigationArr;
};

interface CategoryPageProps {
  tenant?: Maybe<Tenant>;
  algoliaIndexName: string;
  searchClient: any;
  loadingKey: boolean;
  userLoading: boolean;
}

export const CategoryPage: FC<CategoryPageProps> = ({
  tenant,
  algoliaIndexName,
  searchClient,
  loadingKey,
  userLoading,
}) => {
  useScriptLoader(SCRIPTS.TEAMS_SHARE);
  const { t } = useTenantTranslation();
  const { setSearchState, setCategoryTitle } = useDiscoverContext();
  const params = useParams();
  const { category, subcategory } = params;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { redirect } = useSsrContext();

  useRmMetadata({ page: RM_PAGES.CategoryPage });

  const currCategory = subcategory ? subcategory : category;

  const { data: categoryData, loading: loadingCategory } = useQuery<
    Pick<Query, 'getVideoCategory'>,
    QueryGetVideoCategoryArgs
  >(GET_VIDEO_CATEGORY, {
    variables: {
      filter: {
        slug: currCategory,
      },
    },
  });

  const categoryInfo = categoryData?.getVideoCategory;

  const { data: childData, loading: loadingChildData } = useQuery<
    Pick<Query, 'listVideoCategories'>,
    QueryListVideoCategoriesArgs
  >(GET_CATEGORIES_BY_PARENT, {
    variables: {
      filter: {
        parent_id: categoryInfo?.id ? [categoryInfo.id] : null,
      },
    },
    skip: !!subcategory || loadingCategory || !categoryInfo?.id,
  });

  const childCategories = childData?.listVideoCategories;
  const hasChildCategories = childCategories && childCategories.length > 0;
  const hasImage = !!categoryInfo?.image_url;
  const canHaveSustainableGoal =
    !subcategory && TENANTS_CONFIG?.[tenant?.name as TenantsRefs]?.canHaveSustainableGoal;
  const sustainableGoal = categoryInfo?.development_goals?.[0] || null;

  const handleSearch = (val: Maybe<string>) => {
    setSearchState((prevState) => ({
      ...prevState,
      query: val,
    }));
    navigate(`${DISCOVER_SEARCH_ROUTE}?s=${val}`);
  };

  const navigationArr = getNavigationArray(categoryInfo);

  useEffect(() => {
    if (categoryInfo) {
      const categoryObj = {
        [FILTERS.Category]: categoryInfo?.parent
          ? `${categoryInfo?.parent?.id} > ${categoryInfo?.id}`
          : categoryInfo?.id,
      };

      setSearchState((prevState) => ({
        ...prevState,
        hierarchicalMenu: {
          ...categoryObj,
        },
      }));

      if (categoryInfo?.parent) {
        setCategoryTitle(categoryInfo?.parent?.title);
      }
    }
  }, [categoryInfo, setSearchState, setCategoryTitle]);

  const canonicalUrl = getCanonicalUrl(
    categoryInfo?.parent?.slug
      ? buildDiscoverRoute(`${categoryInfo.parent.slug}/${categoryInfo?.slug}`)
      : buildDiscoverRoute(categoryInfo?.slug),
    categoryInfo?.tenants,
  );

  const onChange = useCallback<InstantSearchOnStateChangeProp>(({ uiState, setUiState }) => {
    setUiState(uiState);
  }, []);

  if (categoryInfo) {
    if (!categoryInfo?.parent && category) {
      redirectToNewSlug(redirect, pathname, category, categoryInfo?.slug);
    } else if (
      redirect &&
      (categoryInfo?.parent?.slug !== category || categoryInfo?.slug !== subcategory) &&
      categoryInfo?.parent?.slug
    ) {
      redirect(buildDiscoverRoute(`${categoryInfo.parent.slug}/${categoryInfo.slug}`));
    }
  }

  if (!categoryInfo && !loadingCategory) {
    return (
      <RouteWithCode code={404}>
        <NotFoundPage />
      </RouteWithCode>
    );
  }

  return (
    <>
      {categoryInfo && (
        <SeoBlock
          data={{
            seo_title: generateSeoTitle(
              `${categoryInfo?.title} - ${t('page.discover.learningCourses')}`,
              tenant?.title,
            ),
            seo_description: categoryInfo?.description || '',
            share_image: categoryInfo?.image_url,
          }}
          canonicalUrl={canonicalUrl}
        />
      )}
      <InstantSearch
        searchClient={searchClient}
        indexName={algoliaIndexName}
        onStateChange={onChange}
      >
        <Hero
          title={t('page.discover.discover')}
          imageUrl={categoryInfo?.image_url}
          wrapperCss={hasImage ? headerWrapperCss(hasChildCategories) : undefined}
          contentWrapperCss={hasImage ? headerContentWrapperCss : undefined}
        >
          <CenteredContainer>
            <Search
              refine={handleSearch}
              withClear
              searchPlaceholder={t('page.discover.searchAllCategories')}
            />
            <Tabs />
          </CenteredContainer>
          {(loadingCategory || loadingChildData) && (
            <WithImgContainer hasChildCategories={Boolean(hasChildCategories)}>
              <LoadingBreadcrumbs
                xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
                isSubcategory={Boolean(subcategory)}
              />
              <LoadingTitle xs={SKELETON_TEXT_HEIGHT_PX} />
              <LoadingDescription
                xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
                lines={4}
                spacer={TINY_MARGIN_PX}
              />
              {canHaveSustainableGoal && <SustainableGoal loading />}
              {!subcategory && (
                <SubcategoriesWrapper>
                  <LoadingSubcategory xs={SKELETON_TEXT_HEIGHT_PX} />
                  <LoadingSubcategory xs={SKELETON_TEXT_HEIGHT_PX} />
                  <LoadingSubcategory xs={SKELETON_TEXT_HEIGHT_PX} />
                  <LoadingSubcategory xs={SKELETON_TEXT_HEIGHT_PX} />
                </SubcategoriesWrapper>
              )}
            </WithImgContainer>
          )}
          {!(loadingCategory || loadingChildData) && hasImage && (
            <WithImgContainer hasChildCategories={Boolean(hasChildCategories)}>
              <Breadcrumbs crumbs={navigationArr} data-testid="categoryBreadcrumbs" />
              <CategoryInfo title={categoryInfo?.title} description={categoryInfo?.description} />
              {canHaveSustainableGoal && sustainableGoal && (
                <SustainableGoal
                  description={sustainableGoal?.description}
                  logo={sustainableGoal?.image}
                />
              )}
              {hasChildCategories && (
                <HeaderCategoriesBlock childCategories={childCategories} categorySlug={category} />
              )}
            </WithImgContainer>
          )}
        </Hero>
        {!(loadingCategory || loadingChildData) && (!hasImage || hasChildCategories) && (
          <StyledContainer
            optionalStyles={
              hasImage ? generatedChildCategoriesContainer : generatedWithoutImageContainer
            }
          >
            {!hasImage && (
              <>
                <Breadcrumbs crumbs={navigationArr} data-testid="categoryBreadcrumbs" />
                <CategoryInfo title={categoryInfo?.title} description={categoryInfo?.description} />
                {canHaveSustainableGoal && sustainableGoal && (
                  <SustainableGoal
                    description={sustainableGoal?.description}
                    logo={sustainableGoal?.image}
                  />
                )}
              </>
            )}
            {hasChildCategories && (
              <CategoriesWrapper childCategories={childCategories} categorySlug={category} />
            )}
          </StyledContainer>
        )}
        <Container>
          <SERPContainer
            linkedInId={tenant?.linkedin_id}
            attributesExcludedFromClear={attributesExcludedFromClear}
            loadingApiKey={loadingKey || loadingCategory}
            userLoading={userLoading}
            initialCategory={
              category
                ? categoryInfo?.parent
                  ? `${categoryInfo?.parent?.id} > ${categoryInfo?.id}`
                  : categoryInfo?.id
                : undefined
            }
          />
        </Container>
      </InstantSearch>
    </>
  );
};

export default CategoryPage;

const LoadingBreadcrumbs = styled(AnimatedSkeleton, {
  shouldForwardProp: (prop) => prop !== 'isSubcategory',
})<{ isSubcategory: boolean }>`
  margin-bottom: ${SMALL_MARGIN_PX};
  width: ${({ isSubcategory }) => (isSubcategory ? '350px' : '250px')};
  max-width: 100%;
`;
const LoadingTitle = styled(AnimatedSkeleton)`
  width: 300px;
`;
const LoadingDescription = styled(AnimatedSkeletonMultiline)`
  margin-top: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-top: ${MIDDLE_MARGIN_PX};
  }
`;
const SubcategoriesWrapper = styled('div')`
  margin-top: ${SMALL_MARGIN_PX};
  display: flex;
  flex-wrap: wrap;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-top: ${MIDDLE_MARGIN_PX};
  }

  & > div {
    &:first-child {
      width: 200px;
    }

    &:nth-child(2) {
      width: 310px;
    }

    &:nth-child(3) {
      width: 150px;
    }

    &:nth-child(4) {
      width: 270px;
    }
  }
`;
const LoadingSubcategory = styled(AnimatedSkeleton)`
  margin-bottom: ${TINY_MARGIN_PX};
  margin-right: ${TINY_MARGIN_PX};
`;
const headerWrapperCss =
  (hasChildCategories?: Maybe<boolean>) =>
  ({ theme }) =>
    css`
      margin-bottom: ${hasChildCategories ? 0 : MIDDLE_MARGIN_PX};

      ${theme.breakpoints.up('md')} {
        padding-bottom: 0;
        margin-bottom: ${LARGE_MARGIN_PX};
      }
    `;
const headerContentWrapperCss = css`
  padding-bottom: 0;
`;
const HeaderCategoriesBlock = styled(SubcategoriesBlock)`
  display: none;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: flex;
  }
`;
const CategoriesWrapper = styled(SubcategoriesBlock)`
  ${({ theme }) => theme.breakpoints.up('md')} {
    display: none;
  }
`;
const WithImgContainer = styled(Container, {
  shouldForwardProp: (prop) => prop !== 'hasChildCategories',
})<{ hasChildCategories: boolean }>`
  padding-top: ${MIDDLE_MARGIN_PX};
  padding-bottom: ${({ hasChildCategories }) =>
    hasChildCategories ? SMALL_MARGIN_PX : MIDDLE_MARGIN_PX};

  &:after {
    position: absolute;
    display: ${({ hasChildCategories }) => (!hasChildCategories ? 'block' : 'none')};
    content: '';
    height: 3px;
    width: calc(100% - 32px);
    background-color: ${({ theme }) => theme.palette.common.violet};
    opacity: 0.3;
    left: 16px;
    bottom: 0;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-top: ${LARGE_MARGIN_PX};
    padding-bottom: ${MIDDLE_MARGIN_PX};
    position: relative;

    &:after {
      position: absolute;
      display: block;
      content: '';
      height: 3px;
      width: calc(100% - 32px);
      background-color: ${({ theme }) => theme.palette.common.violet};
      opacity: 0.3;
      left: 16px;
      bottom: 0;
    }
  }
`;
const generatedWithoutImageContainer = ({ theme }) => css`
  position: relative;
  margin-bottom: ${MIDDLE_MARGIN_PX};
  padding-bottom: ${MIDDLE_MARGIN_PX};

  &:after {
    position: absolute;
    display: block;
    content: '';
    height: 3px;
    width: calc(100% - 32px);
    background-color: ${theme.palette.common.violet};
    opacity: 0.3;
    left: 16px;
    bottom: 0;
  }

  ${theme.breakpoints.up('md')} {
    margin-bottom: ${LARGE_MARGIN_PX};
  }
`;
const generatedChildCategoriesContainer = ({ theme }) => css`
  position: relative;
  margin-bottom: ${MIDDLE_MARGIN_PX};

  &:after {
    position: absolute;
    display: block;
    content: '';
    height: 3px;
    width: calc(100% - 32px);
    background-color: ${theme.palette.common.violet};
    opacity: 0.3;
    left: 16px;
    bottom: 0;
  }

  ${theme.breakpoints.up('md')} {
    display: none;
  }
`;
const StyledContainer = styled(Container, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})<{ optionalStyles: CssUtilityType }>`
  ${({ optionalStyles }) => optionalStyles}
`;
